import React, { useMemo } from 'react';
import { shuffle } from 'lodash';
import BrochureHeroCta from './BrochureHeroCta';
import BookmarksScrubber from './BookmarksScrubber';
import AdvisorCtaFooter from './AdvisorCtaFooter';
import BrochureAlumniCarousel from './BrochureAlumniCarousel';
import { LEAD_GEN_BROCHURE_VIEW_SIDEBAR } from '@src/constants/LeadGenerationConstants/index';
import useGraduatesData from '@src/hooks/useGraduatesData';

// Desktop-only sidebar (lg+). On mobile, MobileBrochureSidebar is used
// instead. The layout matches the Figma design: program headline +
// primary CTA at top, bookmarks scrubber in the middle, alumni carousel,
// advisor CTA pinned to the bottom.
//
// The alumni carousel uses InfiniteSlider directly (rather than
// CrackedCrioCareersSection) so we can control the heading and layout for
// the narrow sidebar column instead of inheriting SectionLayout's full-
// page spacing.
const BrochureSidebar = ({
  programName,
  bookmarks,
  currentPage,
  numPages,
  onJumpToPage,
  analytics,
}) => {
  const graduatesData = useGraduatesData();

  const shuffledGraduates = useMemo(
    () => shuffle(graduatesData).slice(0, 5),
    [graduatesData],
  );

  const isPast75Percent = numPages > 0 && currentPage / numPages >= 0.75;

  return (
    <aside className="flex h-full w-full flex-col gap-6 overflow-y-auto bg-v5-green-100 p-6 md:p-8">
      <BrochureHeroCta
        programName={programName}
        buttonLocation={LEAD_GEN_BROCHURE_VIEW_SIDEBAR}
        onCtaClick={analytics?.reportDemoCtaClick}
      />

      {bookmarks && bookmarks.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center">
            <h2 className="font-rubik text-xs uppercase tracking-wide text-v5-neutral-400">
              Read till the End!
            </h2>
          </div>
          <BookmarksScrubber
            currentPage={currentPage}
            numPages={numPages}
          />
        </div>
      )}

      {graduatesData.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="font-manrope text-sm font-bold text-v5-neutral-600">
            Crio Grads have Cracked their Dream Careers In
          </h2>
          <div className="overflow-hidden">
            <BrochureAlumniCarousel data={shuffledGraduates} />
          </div>
        </div>
      )}

      <div className="mt-auto">
        <AdvisorCtaFooter
          onCtaClick={analytics?.reportAdvisorCtaClick}
          isGlowing={isPast75Percent}
        />
      </div>
    </aside>
  );
};

export default React.memo(BrochureSidebar);
